import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import Navbar from "../../layout/Navbar"
import Footer from "../../layout/Footer"
import SEO from "../../components/SEO"
import Hatchback from "../../assets/cars/Hatchback.png"
import Sedan from "../../assets/cars/Sedan.png"
import SUV from "../../assets/cars/SUv.png"
import "./cabEstimatePage.scss"

// ── Helpers ───────────────────────────────────────────────────────────────────

const VEHICLE_ORDER = ["hatch", "sedan", "suv", "innova", "ertiga"]

const getVehicleImage = (name = "") => {
  if (!name) return Sedan
  const n = name.toLowerCase()
  if (n.includes("hatch")) return Hatchback
  if (n.includes("suv") || n.includes("innova") || n.includes("ertiga")) return SUV
  return Sedan
}

const sortVehicles = (list) =>
  [...list].sort((a, b) => {
    const ai = VEHICLE_ORDER.findIndex((v) => a.name.toLowerCase().includes(v))
    const bi = VEHICLE_ORDER.findIndex((v) => b.name.toLowerCase().includes(v))
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi)
  })

const fmt = (val, decimals = 0) =>
  "₹" + parseFloat(val || 0).toLocaleString("en-IN", { maximumFractionDigits: decimals })

// Proper inline icons (replaces the emoji): size with the surrounding font.
const IcRuler = () => (
  <svg className="cabEstimate__ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="8" width="18" height="8" rx="1.5" /><path d="M7 8v3M11 8v4M15 8v3M19 8v4" />
  </svg>
)
const IcUsers = () => (
  <svg className="cabEstimate__ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────

const CabEstimatePage = () => {
  const { uniqueId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [distance, setDistance] = useState(null)

  useEffect(() => {
    if (!uniqueId) { navigate("/"); return }
    fetchTripData()
  }, [uniqueId])

  const fetchTripData = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/website/visitors_trip_fare`,
        { visitor_unique_id: uniqueId }
      )

      if (response?.data?.status === 200 && response?.data?.data?.length) {
        setVehicles(sortVehicles(response.data.data))
        setDistance(response.data.distance ?? null)
      } else {
        setError("Trip details not found. The link may have expired.")
      }
    } catch (err) {
      console.error("Error fetching trip data:", err)
      setError("Unable to load trip details. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="cabEstimate">
      <Navbar />
      <div className="cabEstimate__loading">
        <div className="cabEstimate__spinner" />
        <p>Loading your fare estimate…</p>
      </div>
      <Footer />
    </div>
  )

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error || !vehicles.length) return (
    <div className="cabEstimate">
      <SEO title="Fare Estimate | Pulpit Cabs" description="View your cab fare estimate" />
      <Navbar />
      <div className="cabEstimate__error">
        <div className="cabEstimate__error-icon">Not Found</div>
        <h2>Estimate Not Found</h2>
        <p>{error || "This fare estimate link is invalid or has expired."}</p>
        <button
          className="cabEstimate__btn cabEstimate__btn--primary"
          onClick={() => navigate("/")}
        >
          Book a New Cab
        </button>
      </div>
      <Footer />
    </div>
  )

  // ── Main ─────────────────────────────────────────────────────────────────────
  return (
    <div className="cabEstimate">
      <SEO
        title="Fare Estimate | Pulpit Cabs"
        description="View your cab fare estimate with Pulpit Cabs."
        keywords="cab fare estimate, pulpit cabs, taxi fare"
        noindex
      />
      <Navbar />

      <div className="cabEstimate__container">

        {/* ── Page Header ── */}
        <div className="cabEstimate__header">
          <h1 className="cabEstimate__title">Your Fare Estimate</h1>
          {distance != null && (
            <span className="cabEstimate__subtitle">
              <IcRuler /> Total Distance: {parseFloat(distance).toFixed(1)} KM
            </span>
          )}
        </div>

        {/* ── Vehicle Cards ── */}
        <div className="cabEstimate__vehicle-list">
          {vehicles.map((v) => {
            const advance = (parseFloat(v.total) * parseFloat(v.advance_booking)) / 100
            return (
              <div key={v.vehicle_id} className="cabEstimate__vehicle-card">

                {/* Left: image */}
                <div className="cabEstimate__vehicle-visual">
                  <img
                    src={getVehicleImage(v.name)}
                    alt={v.name}
                    className="cabEstimate__vehicle-img"
                  />
                </div>

                {/* Centre: name + chips */}
                <div className="cabEstimate__vehicle-details">
                  <div className="cabEstimate__vehicle-name">{v.name}</div>
                  <div className="cabEstimate__vehicle-chips">
                    <span className="cabEstimate__chip"><IcUsers /> {v.seat_capacity} Seats</span>
                    <span className="cabEstimate__chip">₹{v.fare_per_km}/km</span>
                    {v.base_km_per_day > 0 && (
                      <span className="cabEstimate__chip"><IcRuler /> {v.base_km_per_day} km/day</span>
                    )}
                  </div>
                </div>

                {/* Right: fare panel */}
                <div className="cabEstimate__fare-panel">

                  {/* sub-rows — hidden on mobile */}
                  <div className="cabEstimate__fare-row">
                    <span>Subtotal</span>
                    <span>{fmt(v.subtotal)}</span>
                  </div>
                  <div className="cabEstimate__fare-row">
                    <span>GST ({v.gst_percentage}%)</span>
                    <span>{fmt(v.gst)}</span>
                  </div>
                  {parseFloat(v.driver_allowance) > 0 && (
                    <div className="cabEstimate__fare-row">
                      <span>Driver Allowance</span>
                      <span>{fmt(v.driver_allowance)}</span>
                    </div>
                  )}

                  <div className="cabEstimate__fare-divider" />

                  {/* Total row */}
                  <div className="cabEstimate__fare-total-row">
                    <span className="cabEstimate__fare-label">Total</span>
                    <span className="cabEstimate__fare-amount">{fmt(v.total)}</span>
                  </div>

                  {/* Advance note */}
                  <div className="cabEstimate__advance-note">
                    Pay now: <span>{fmt(advance)}</span> ({v.advance_booking}% advance)
                  </div>
                </div>

              </div>
            )
          })}
        </div>

        {/* ── CTA ── */}
        <div className="cabEstimate__cta">
          <button
            className="cabEstimate__btn cabEstimate__btn--primary"
            onClick={() => navigate("/")}
          >
             Book a New Cab
          </button>
          <button
            className="cabEstimate__btn cabEstimate__btn--outline"
            onClick={() => navigate(-1)}
          >
            ← Go Back
          </button>
        </div>

        {/* ── Help ── */}
        <div className="cabEstimate__help">
          <p>Need help? Call us at <a href="tel:+918052080501">+91 80520 80501</a></p>
        </div>

      </div>

      <Footer />
    </div>
  )
}

export default CabEstimatePage